import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import ipponApi from "../config/axios";
import { getAuthToken } from "@/utils/authUtils";
import { resolveAiBlockedMessage } from "@/constants/clientAccess";
import { addToast } from "./toastSlice";
import type { PageContext } from "@ippon/shared-utils";

const BaseURL = import.meta.env.VITE_API_URL;
const AGENT_COMMAND_PREFIX = "/agent";
const AGENT_CONFIRM_PREFIX = "/confirm";
const AGENT_REJECT_PREFIX = "/reject";

// Types
export interface ExtractedDate {
  date: string;
  context: string;
  confidence?: number;
}

export interface ExtractedDates {
  dates: ExtractedDate[];
}

export interface ReferencedDocument {
  document_id: string;
  document_name: string;
  document_type: string;
  file_url: string;
  property_id?: string;
  property_name?: string;
  page_number?: number;
  similarity_score?: number;
  dates?: ExtractedDates;
  // 出所の種別とエンティティ（カードのラベル表示・種別別遷移用）
  source_type?: "property_doc" | "contract_doc" | "repair_estimate" | "expense_doc";
  entity_label?: string;
  unit_id?: string;
  contract_id?: string;
  repair_id?: string;
  expense_id?: string;
}

export interface ChatFileAttachment {
  id: string;
  name: string;
  type: string;
  url?: string;
}

export type PendingActionStatus =
  | "pending"
  | "executing"
  | "executed"
  | "rejected"
  | "failed";

export interface PendingAction {
  action_id: string;
  tool_name: string;
  status: PendingActionStatus;
}

export interface ChatMessage {
  message_id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  referenced_documents?: ReferencedDocument[];
  confidence?: number;
  files?: ChatFileAttachment[];
  pending_action?: PendingAction;
}

const buildOptimisticActionMessageId = (
  kind: "confirm" | "reject",
  actionId: string,
): string => `user-action-${kind}-${actionId}`;

const createOptimisticActionMessage = (
  kind: "confirm" | "reject",
  actionId: string,
): ChatMessage => ({
  message_id: buildOptimisticActionMessageId(kind, actionId),
  role: "user",
  content: kind === "confirm" ? "承認" : "キャンセル",
  timestamp: new Date().toISOString(),
});

export interface SendMessageRequest {
  query: string;
  thread_id?: string; // スレッドID（undefinedの場合は"new"として送信）
  property_id?: string;
  files?: File[];
  /** 画面コンテキスト (data-entity/data-id/data-field 抽出結果)。
   *  BE には先頭プロンプトとして埋め込み、完了時の refetchBus.emit にも使う。 */
  pageContext?: PageContext;
}

export interface SendMessageResponse {
  thread_id?: string; // スレッドID（新規作成時は生成されたID）
  answer: string;
  referenced_documents: ReferencedDocument[];
  confidence?: number;
  pending_action?: PendingAction;
}

export interface AgentActionRequest {
  action_id: string;
  source_message_id?: string;
}

export interface GetMessagesRequest {
  limit?: number;
  next_token?: string;
  thread_id?: string; // スレッドID（オプション）
}

export interface GetMessagesResponse {
  messages: ChatMessage[];
  next_token?: string;
}

export interface DeleteMessagesRequest {
  thread_id?: string;
  role?: "assistant" | "user";
  latest_user_message_content?: string;
}

export interface DeleteMessagesResponse {
  success: boolean;
  deleted_count: number;
}

export interface ChatThread {
  thread_id: string;
  title: string;
  created_at: string;
  last_message_timestamp: string;
  last_message_content?: string;
  message_count: number;
}

export interface GetThreadsRequest {
  limit?: number;
  next_token?: string;
}

export interface GetThreadsResponse {
  threads: ChatThread[];
  next_token?: string;
}

interface ChatState {
  messages: ChatMessage[];
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  // スレッドの会話取得(getChatMessages)専用のエラー。
  // 「開いたのに白画面」を防ぐため、メッセージエリアの失敗表示に使う。
  messagesError: string | null;
  nextToken?: string;
  hasMore: boolean;
  threads: ChatThread[];
  threadsNextToken?: string;
  threadsHasMore: boolean;
  isLoadingThreads: boolean;
  // フローティングチャット(AIChatButton)を外部から開くための橋渡し。
  // - threadId 指定で既存スレッドを開く
  // - sendQuery 指定で、開いた直後にその文言を送信する（ホームのランチャー用）
  // - expand=true で全画面(大画面)で開く
  // AIChatButton が購読して開いたら consumeChatOpen で null に戻す。
  pendingChatOpen: {
    threadId?: string;
    sendQuery?: string;
    expand?: boolean;
    // 開くアニメーションの起点（ビューポート座標）。Home の入力位置など。
    originX?: number;
    originY?: number;
  } | null;
}

const initialState: ChatState = {
  messages: [],
  isLoading: false,
  isSending: false,
  error: null,
  messagesError: null,
  nextToken: undefined,
  hasMore: true,
  threads: [],
  threadsNextToken: undefined,
  threadsHasMore: true,
  isLoadingThreads: false,
  pendingChatOpen: null,
};

/** 履歴再取得時に、承認待ち状態だけクライアント側から復元する */
function mergePendingActionsFromExisting(
  existingMessages: ChatMessage[],
  incomingMessages: ChatMessage[],
): ChatMessage[] {
  const pendingByContent = new Map<string, PendingAction>();
  for (const message of existingMessages) {
    if (message.pending_action?.status === "pending") {
      pendingByContent.set(message.content, message.pending_action);
    }
  }
  if (pendingByContent.size === 0) {
    return incomingMessages;
  }
  return incomingMessages.map((message) => {
    const pending = pendingByContent.get(message.content);
    return pending ? { ...message, pending_action: pending } : message;
  });
}

// Async Thunks

// チャット送信
export const sendChatMessage = createAsyncThunk<
  { message: ChatMessage; response: SendMessageResponse },
  SendMessageRequest
>("chat/sendMessage", async (request, { rejectWithValue, signal, dispatch }) => {
  try {
    const trimmedQuery = request.query.trim();

    if (!request.files?.length && trimmedQuery.startsWith(AGENT_CONFIRM_PREFIX)) {
      const actionId = trimmedQuery.slice(AGENT_CONFIRM_PREFIX.length).trim();
      if (!actionId) {
        return rejectWithValue("action_id is required");
      }
      const response = await ipponApi.post(
        `${BaseURL}/api/v1/agent/actions/${actionId}/confirm`,
        {},
        { signal }
      );
      const assistantMessage: ChatMessage = {
        message_id: `assistant-${Date.now()}`,
        role: "assistant",
        content: response.data.message || "操作を実行しました",
        timestamp: new Date().toISOString(),
      };
      return {
        message: assistantMessage,
        response: {
          answer: assistantMessage.content,
          referenced_documents: [],
        },
      };
    }

    if (!request.files?.length && trimmedQuery.startsWith(AGENT_REJECT_PREFIX)) {
      const actionId = trimmedQuery.slice(AGENT_REJECT_PREFIX.length).trim();
      if (!actionId) {
        return rejectWithValue("action_id is required");
      }
      const response = await ipponApi.post(
        `${BaseURL}/api/v1/agent/actions/${actionId}/reject`,
        {},
        { signal }
      );
      const assistantMessage: ChatMessage = {
        message_id: `assistant-${Date.now()}`,
        role: "assistant",
        content: response.data.message || "操作をキャンセルしました",
        timestamp: new Date().toISOString(),
      };
      return {
        message: assistantMessage,
        response: {
          answer: assistantMessage.content,
          referenced_documents: [],
        },
      };
    }

    const baseMessage = trimmedQuery.startsWith(AGENT_COMMAND_PREFIX)
      ? trimmedQuery.slice(AGENT_COMMAND_PREFIX.length).trim()
      : trimmedQuery;
    const hasFiles = !!request.files && request.files.length > 0;
    if (!baseMessage && !hasFiles) {
      return rejectWithValue("message is required");
    }
    // 画面コンテキスト (request.pageContext) は本文に埋め込まない。
    // 理由: BE が user メッセージとして保存し、チャット UI に JSON がそのまま表示されてしまうため。
    // BE 側が page_context フィールドを別途受け取れるようになるまで、FE では refetchBus.emit のみに使う。
    const agentMessage = baseMessage
      ? request.property_id
        ? `[コンテキスト] property_id=${request.property_id}\n\n${baseMessage}`
        : baseMessage
      : request.property_id
        ? `[コンテキスト] property_id=${request.property_id}`
        : "";

    let agentPayload: any;
    // const agentConfig: any = { signal };
    const agentConfig: any = { signal, timeout: 300000 };                   
   
  
    if (request.files && request.files.length > 0) {
      const formData = new FormData();
      formData.append("message", agentMessage);
      if (request.thread_id) {
        formData.append("thread_id", request.thread_id);
      }
      const fileMetadata = request.files.map((file) => ({
        name: file.name,
        type: file.type,
      }));
      formData.append("file_metadata", JSON.stringify(fileMetadata));
      request.files.forEach((file) => {
        formData.append("files", file);
      });
      agentPayload = formData;
      agentConfig.headers = { "Content-Type": "multipart/form-data" };
    } else {
      // 画面コンテキストは本文には混ぜず、独立フィールド page_context として BE へ送る。
      // BE は user メッセージに紐づけて DynamoDB に保存し、Function Calling
      // `get_current_page_context` で必要時のみ取り出される。
      agentPayload = {
        message: agentMessage,
        thread_id: request.thread_id,
        page_context: request.pageContext,
      };
    }

    const response = await ipponApi.post(
      `${BaseURL}/api/v1/agent/messages`,
      agentPayload,
      agentConfig,
    );
    const pendingAction =
      response.data.type === "approval_required"
        ? {
            action_id: response.data.action_id,
            tool_name: response.data.tool_name,
            status: "pending" as const,
          }
        : undefined;
    const answer = response.data.message;
    // オーケストレータ(/agent/messages)が返す参照書類(PDF URL・書類名・物件名)を
    // メッセージに載せる。AIChatButton で referencedDocuments にマップされ、
    // CustomMessage が出典として描画する。
    const referencedDocuments: ReferencedDocument[] =
      response.data.referenced_documents ?? [];
    const assistantMessage: ChatMessage = {
      message_id: `assistant-${Date.now()}`,
      role: "assistant",
      content: answer,
      timestamp: new Date().toISOString(),
      referenced_documents: referencedDocuments,
      pending_action: pendingAction,
    };
    return {
      message: assistantMessage,
      response: {
        thread_id: response.data.thread_id,
        answer,
        referenced_documents: referencedDocuments,
        pending_action: pendingAction,
      },
    };
  } catch (error: any) {
    if (error.name === "CanceledError" || error.name === "AbortError") {
      throw error;
    }
    // AI を止めた 403（トライアルの無料枠を使い切った・解約後の猶予期間）は、理由を画面に出す
    const aiBlockedMessage = resolveAiBlockedMessage(error.response?.data);
    if (aiBlockedMessage) {
      dispatch(addToast({ message: aiBlockedMessage, type: "error" }));
    }
    return rejectWithValue(
      error.response?.data?.error || "Failed to send message"
    );
  }
});

export const warmupAgent = createAsyncThunk<void, void>(
  "chat/warmupAgent",
  async (_, { signal }) => {
    const token = getAuthToken();
    if (!token) return;

    try {
      await axios.post(
        `${BaseURL}/api/v1/agent/warmup`,
        {},
        {
          signal,
          timeout: 8000,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    } catch {
      // ベストエフォートのwarmupなので、UI状態には反映しない。
    }
  },
);

export const confirmChatAction = createAsyncThunk<
  {
    assistantMessage: ChatMessage;
    action_id: string;
    source_message_id?: string;
  },
  AgentActionRequest
>("chat/confirmAction", async (request, { rejectWithValue, signal }) => {
  try {
    const response = await ipponApi.post(
      `${BaseURL}/api/v1/agent/actions/${request.action_id}/confirm`,
      {},
      { signal },
    );
    const assistantMessage: ChatMessage = {
      message_id: `assistant-${Date.now()}`,
      role: "assistant",
      content: response.data.message || "操作を実行しました",
      timestamp: new Date().toISOString(),
    };
    return {
      assistantMessage,
      action_id: request.action_id,
      source_message_id: request.source_message_id,
    };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.error || "操作の実行に失敗しました",
    );
  }
});

export const rejectChatAction = createAsyncThunk<
  {
    assistantMessage: ChatMessage;
    action_id: string;
    source_message_id?: string;
  },
  AgentActionRequest
>("chat/rejectAction", async (request, { rejectWithValue, signal }) => {
  try {
    const response = await ipponApi.post(
      `${BaseURL}/api/v1/agent/actions/${request.action_id}/reject`,
      {},
      { signal },
    );
    const assistantMessage: ChatMessage = {
      message_id: `assistant-${Date.now()}`,
      role: "assistant",
      content: response.data.message || "操作をキャンセルしました",
      timestamp: new Date().toISOString(),
    };
    return {
      assistantMessage,
      action_id: request.action_id,
      source_message_id: request.source_message_id,
    };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.error || "操作のキャンセルに失敗しました",
    );
  }
});

// メッセージ履歴取得
export const getChatMessages = createAsyncThunk<
  GetMessagesResponse,
  GetMessagesRequest
>("chat/getMessages", async (request, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams({
      limit: (request.limit || 20).toString(),
    });

    if (request.next_token) {
      params.append("next_token", request.next_token);
    }

    // thread_idが指定されている場合は、スレッド専用エンドポイントを使用
    let url: string;
    if (request.thread_id) {
      url = `${BaseURL}/api/v1/chat/threads/${request.thread_id}/messages?${params.toString()}`;
    } else {
      url = `${BaseURL}/api/v1/chat/messages?${params.toString()}`;
    }

    const response = await ipponApi.get(url);

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.error || "Failed to fetch messages"
    );
  }
});

// メッセージ履歴削除
export const deleteChatMessages = createAsyncThunk<
  DeleteMessagesResponse,
  DeleteMessagesRequest | void
>("chat/deleteMessages", async (request, { rejectWithValue }) => {
  try {
    let url = `${BaseURL}/api/v1/chat/messages`;
    const params = new URLSearchParams();
    
    if (request?.thread_id) {
      if (request.role === "assistant") {
        url = `${BaseURL}/api/v1/chat/threads/${request.thread_id}/messages/assistant`;
        if (request.latest_user_message_content) {
          params.append("latest_user_message_content", request.latest_user_message_content);
        }
      } else {
        url = `${BaseURL}/api/v1/chat/threads/${request.thread_id}/messages`;
      }
    }
    
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
    
    const response = await ipponApi.delete(url);

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.error || "Failed to delete messages"
    );
  }
});

// スレッド一覧取得
export const getChatThreads = createAsyncThunk<
  GetThreadsResponse,
  GetThreadsRequest
>("chat/getThreads", async (request, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams();
    if (request.limit) {
      params.append("limit", request.limit.toString());
    }
    if (request.next_token) {
      params.append("next_token", request.next_token);
    }

    const queryString = params.toString();
    const url = queryString
      ? `${BaseURL}/api/v1/chat/threads?${queryString}`
      : `${BaseURL}/api/v1/chat/threads`;

    const response = await ipponApi.get(url);

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.error || "Failed to fetch threads"
    );
  }
});

// Slice
const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    // メッセージをクリア
    clearMessages: (state) => {
      state.messages = [];
      state.nextToken = undefined;
      state.hasMore = true;
      state.error = null;
      state.messagesError = null;
    },
    // エラーをクリア
    clearError: (state) => {
      state.error = null;
    },
    // 指定されたIDのアシスタントメッセージを削除
    removeAssistantMessageById: (
      state,
      action: PayloadAction<{ messageId: string }>
    ) => {
      state.messages = state.messages.filter(
        (msg) => !(msg.message_id === action.payload.messageId && msg.role === "assistant")
      );
    },
    // ユーザーメッセージを即座に追加（楽観的UI更新）
    addUserMessage: (
      state,
      action: PayloadAction<{ content: string; files?: ChatFileAttachment[] }>
    ) => {
      const userMessage: ChatMessage = {
        message_id: `temp-user-${Date.now()}`,
        role: "user",
        content: action.payload.content,
        timestamp: new Date().toISOString(),
        files:
          action.payload.files && action.payload.files.length > 0
            ? action.payload.files
            : undefined,
      };
      state.messages.push(userMessage);
    },
    // チャットを開く要求。
    // - threadId 未指定は新規チャット
    // - sendQuery 指定で開いた直後に送信（ホームのランチャー）
    // - expand=true で全画面
    requestChatOpen: (
      state,
      action: PayloadAction<
        | {
            threadId?: string;
            sendQuery?: string;
            expand?: boolean;
            originX?: number;
            originY?: number;
          }
        | undefined
      >
    ) => {
      state.pendingChatOpen = {
        threadId: action.payload?.threadId,
        sendQuery: action.payload?.sendQuery,
        expand: action.payload?.expand,
        originX: action.payload?.originX,
        originY: action.payload?.originY,
      };
    },
    // 開く要求を消費（AIChatButton が開いた後に呼ぶ）。
    consumeChatOpen: (state) => {
      state.pendingChatOpen = null;
    },
  },
  extraReducers: (builder) => {
    // sendChatMessage
    builder
      .addCase(sendChatMessage.pending, (state) => {
        state.isSending = true;
        state.error = null;
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        state.isSending = false;
        // アシスタントメッセージを追加
        state.messages.push(action.payload.message);
        // refetchBus.emit は呼ばない。reducer 内の同期 emit から useRefetchOn の callback で
        // dispatch が走るとレースになり、fetchPropertyDetail が失敗するため。
        // emit は AIChatButton 側で thunk 完了を await した後に行う。
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        state.isSending = false;
        // キャンセル/中断の場合はエラーメッセージを設定しない
        const errorName = action.error?.name;
        if (errorName === 'AbortError' || errorName === 'CanceledError') {
          // キャンセルは正常な操作なのでエラー扱いしない
          return;
        }
        state.error = action.payload as string;
      });

    builder
      .addCase(confirmChatAction.pending, (state, action) => {
        state.isSending = true;
        state.error = null;
        const message = state.messages.find(
          (msg) =>
            msg.message_id === action.meta.arg.source_message_id ||
            msg.pending_action?.action_id === action.meta.arg.action_id,
        );
        if (message?.pending_action) {
          message.pending_action.status = "executing";
        }
        const optimisticMessageId = buildOptimisticActionMessageId(
          "confirm",
          action.meta.arg.action_id,
        );
        if (
          !state.messages.some(
            (msg) => msg.message_id === optimisticMessageId,
          )
        ) {
          state.messages.push(
            createOptimisticActionMessage("confirm", action.meta.arg.action_id),
          );
        }
      })
      .addCase(confirmChatAction.fulfilled, (state, action) => {
        state.isSending = false;
        const message = state.messages.find(
          (msg) =>
            msg.message_id === action.payload.source_message_id ||
            msg.pending_action?.action_id === action.payload.action_id,
        );
        if (message?.pending_action) {
          message.pending_action.status = "executed";
        }
        state.messages.push(action.payload.assistantMessage);
      })
      .addCase(confirmChatAction.rejected, (state, action) => {
        state.isSending = false;
        const optimisticMessageId = buildOptimisticActionMessageId(
          "confirm",
          action.meta.arg.action_id,
        );
        state.messages = state.messages.filter(
          (msg) => msg.message_id !== optimisticMessageId,
        );
        const message = state.messages.find(
          (msg) =>
            msg.message_id === action.meta.arg.source_message_id ||
            msg.pending_action?.action_id === action.meta.arg.action_id,
        );
        if (message?.pending_action) {
          message.pending_action.status = "failed";
        }
        state.error = action.payload as string;
      });

    builder
      .addCase(rejectChatAction.pending, (state, action) => {
        state.isSending = true;
        state.error = null;
        const message = state.messages.find(
          (msg) =>
            msg.message_id === action.meta.arg.source_message_id ||
            msg.pending_action?.action_id === action.meta.arg.action_id,
        );
        if (message?.pending_action) {
          message.pending_action.status = "executing";
        }
        const optimisticMessageId = buildOptimisticActionMessageId(
          "reject",
          action.meta.arg.action_id,
        );
        if (
          !state.messages.some(
            (msg) => msg.message_id === optimisticMessageId,
          )
        ) {
          state.messages.push(
            createOptimisticActionMessage("reject", action.meta.arg.action_id),
          );
        }
      })
      .addCase(rejectChatAction.fulfilled, (state, action) => {
        state.isSending = false;
        const message = state.messages.find(
          (msg) =>
            msg.message_id === action.payload.source_message_id ||
            msg.pending_action?.action_id === action.payload.action_id,
        );
        if (message?.pending_action) {
          message.pending_action.status = "rejected";
        }
        state.messages.push(action.payload.assistantMessage);
      })
      .addCase(rejectChatAction.rejected, (state, action) => {
        state.isSending = false;
        const optimisticMessageId = buildOptimisticActionMessageId(
          "reject",
          action.meta.arg.action_id,
        );
        state.messages = state.messages.filter(
          (msg) => msg.message_id !== optimisticMessageId,
        );
        const message = state.messages.find(
          (msg) =>
            msg.message_id === action.meta.arg.source_message_id ||
            msg.pending_action?.action_id === action.meta.arg.action_id,
        );
        if (message?.pending_action) {
          message.pending_action.status = "failed";
        }
        state.error = action.payload as string;
      });

    // getChatMessages
    builder
      .addCase(getChatMessages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.messagesError = null;
      })
      .addCase(getChatMessages.fulfilled, (state, action) => {
        state.isLoading = false;
        state.messagesError = null;
        const request = action.meta.arg;

        // next_tokenがある場合は追加読み込み（スクロール時）
        if (request.next_token) {
          // 重複を防ぐために、既存のmessage_idをSetで管理
          const existingIds = new Set(state.messages.map(m => m.message_id));
          const newMessages = action.payload.messages.filter(
            m => !existingIds.has(m.message_id)
          );
          // APIから降順で返ってくるので、逆順にしてから先頭に追加
          const sortedMessages = mergePendingActionsFromExisting(
            state.messages,
            [...newMessages].reverse(),
          );
          state.messages = [...sortedMessages, ...state.messages];
        } else if (request.thread_id) {
          const sortedMessages = mergePendingActionsFromExisting(
            state.messages,
            [...action.payload.messages].reverse(),
          );
          state.messages = sortedMessages;
        } else {
          const sortedMessages = mergePendingActionsFromExisting(
            state.messages,
            [...action.payload.messages].reverse(),
          );
          state.messages = sortedMessages;
        }
        state.nextToken = action.payload.next_token;
        state.hasMore = !!action.payload.next_token;
      })
      .addCase(getChatMessages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.messagesError =
          (action.payload as string) || "会話の読み込みに失敗しました";
      });

    // deleteChatMessages
    builder
      .addCase(deleteChatMessages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteChatMessages.fulfilled, (state) => {
        state.isLoading = false;
        state.messages = [];
        state.nextToken = undefined;
        state.hasMore = true;
      })
      .addCase(deleteChatMessages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // getChatThreads
    builder
      .addCase(getChatThreads.pending, (state) => {
        state.isLoadingThreads = true;
        state.error = null;
      })
      .addCase(getChatThreads.fulfilled, (state, action) => {
        state.isLoadingThreads = false;
        if (action.payload.threads) {
          const request = action.meta.arg;

          if (request.next_token) {
            // 追加読み込みの場合：既存のスレッドに追加（重複を防ぐ）
            const existingThreadIds = new Set(state.threads.map(t => t.thread_id));
            const newThreads = action.payload.threads.filter(
              t => !existingThreadIds.has(t.thread_id)
            );
            state.threads = [...state.threads, ...newThreads];
          } else {
            // 初回読み込みの場合：重複を除去してから設定
            // 新しいスレッドが先頭に来るように、作成日時でソート（降順）
            const uniqueThreads = action.payload.threads.filter(
              (thread, index, self) =>
                index === self.findIndex(t => t.thread_id === thread.thread_id)
            );
            uniqueThreads.sort((a, b) => {
              const dateA = new Date(a.last_message_timestamp || a.created_at).getTime();
              const dateB = new Date(b.last_message_timestamp || b.created_at).getTime();
              return dateB - dateA;
            });
            state.threads = uniqueThreads;
          }
        }
        state.threadsNextToken = action.payload.next_token;
        state.threadsHasMore = !!action.payload.next_token;
      })
      .addCase(getChatThreads.rejected, (state, action) => {
        state.isLoadingThreads = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearMessages,
  clearError,
  addUserMessage,
  removeAssistantMessageById,
  requestChatOpen,
  consumeChatOpen,
} = chatSlice.actions;
export default chatSlice.reducer;
